import React, { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { AnyState } from 'common/bento/types/Abstract';
import SuspenseQuery from 'components/SuspenseQuery';

import { BentoModuleDoneStatus } from '../../../../common/bento/types/BentoModule';
import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import CapitalDepositDraftStatutes from '../CapitalDepositDraftStatutes';
import {
  GetCapitalDepositQuestionsDocument,
  GetCapitalDepositQuestionsQuery,
} from './graphql/queries/getCapitalDepositQuestions.gql';
import machineConfig, { model, State } from './machine';
import { CapitalDepositQuestionsModuleData } from './machine/model';
import { AddressState } from './machine/state';
import CapitalInfo from './pages/CapitalInfo';
import CertificateWarning from './pages/CertificateWarning';
import CompanyAddress from './pages/CompanyAddress';
import CompanyLegalInfo from './pages/CompanyLegalInfo';
import HasPartner from './pages/HasPartner';
import Intro from './pages/Intro';
import PartnerName from './pages/PartnerName';
import SameAddress from './pages/SameAddress';

type StringifiedState =
  /**
   * a State can be any top-level State while excluding sub-states' parent
   * here State.ACCOUNTANT is the parent of the AccountantState
   */
  | Exclude<State, State.ADDRESS>
  /**
   * or any sub-state, prefixed by its parent's name
   * e.g. accoutant.features
   */
  | `${State.ADDRESS}.${AddressState}`;

const isModuleDataValid = (
  data: GetCapitalDepositQuestionsQuery,
): data is CapitalDepositQuestionsModuleData['data'] =>
  Boolean(data.viewer.company.capitalDeposit);

/**
 * Compute if the user went through a partner
 */
const getHasPartner = (partner: string | null) => {
  if (partner === null) {
    return undefined;
  }

  return partner !== '';
};

interface ProfileWithAddress {
  street: string | null;
  city: string | null;
  zip: string | null;
  streetAddition: string | null;
  country: string | null;
}

/**
 * Compute if the company address and user address are the same
 */
const getHasSameAddress = ({
  companyProfile,
  userProfile,
}: {
  companyProfile: ProfileWithAddress;
  userProfile: ProfileWithAddress;
}): boolean | undefined => {
  if (!companyProfile.street && !companyProfile.city && !companyProfile.zip) {
    return undefined;
  }

  return (
    userProfile.street === companyProfile.street &&
    userProfile.streetAddition === companyProfile.streetAddition &&
    userProfile.city === companyProfile.city &&
    userProfile.zip === companyProfile.zip
  );
};

const ORDERED_STATES = [
  [State.INTRO],
  [State.COMPANY_LEGAL_INFO, State.SEND_COMPANY_LEGAL_INFO],
  [State.CAPITAL_INFO, State.SEND_CAPITAL_INFO],
  [State.HAS_PARTNER, State.SEND_HAS_NO_PARTNER],
  [State.PARTNER_NAME, State.SEND_PARTNER_NAME],
  [
    `${State.ADDRESS}.${AddressState.SAME_ADDRESS}`,
    `${State.ADDRESS}.${AddressState.SEND_SAME_ADDRESS}`,
  ],
  [
    `${State.ADDRESS}.${AddressState.COMPANY_ADDRESS}`,
    `${State.ADDRESS}.${AddressState.SEND_COMPANY_ADDRESS}`,
  ],
  [State.CERTIFICATE_WARNING],
];

/**
 * Compute the progress of a given state
 * Returns a number between 0 and 1
 */
const getProgress = (state: AnyState): number => {
  const currentStateIndex = ORDERED_STATES.findIndex((states) =>
    states.some((s) => state.matches(s)),
  );

  return currentStateIndex / (ORDERED_STATES.length - 1);
};

const CapitalDepositQuestionsComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => {
      send(model.events.PREV());
    }, [send]);

    const stringifiedState = stringifyState<StringifiedState>(state);

    const { data, isMandatee } = moduleData;
    const { company, profile: userProfile } = data.viewer;
    const { capitalDeposit, profile: companyProfile } = company;
    const progress = getProgress(state);
    const hasSameAddress = getHasSameAddress({
      companyProfile,
      userProfile,
    });

    switch (stringifiedState) {
      case State.INTRO:
        return (
          <Intro
            firstName={userProfile.firstName}
            isMandatee={isMandatee}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.COMPANY_LEGAL_INFO:
      case State.SEND_COMPANY_LEGAL_INFO:
        return (
          <CompanyLegalInfo
            formData={{
              inseeLegalFormCode:
                company.profile.inseeLegalFormCode || undefined,
              legalName: company.profile.legalName,
            }}
            isMandatee={isMandatee}
            isSending={state.matches(State.SEND_COMPANY_LEGAL_INFO)}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SUBMIT_COMPANY_LEGAL_INFO(values))
            }
            progress={progress}
          />
        );

      case State.CAPITAL_INFO:
      case State.SEND_CAPITAL_INFO:
        return (
          <CapitalInfo
            formData={{
              legalNumberOfShares:
                companyProfile.legalNumberOfShares || undefined,
              // TODO: set this field to null in the DB instead of 0 at creation.
              legalShareCapital: companyProfile.legalShareCapital || undefined,
              paidUpCapitalPercentage:
                capitalDeposit.paidUpCapitalPercentage || undefined,
            }}
            inseeLegalFormCode={company.profile.inseeLegalFormCode}
            isMandatee={isMandatee}
            isSending={state.matches(State.SEND_CAPITAL_INFO)}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SUBMIT_CAPITAL_INFO(values))
            }
            progress={progress}
          />
        );

      case State.HAS_PARTNER:
      case State.SEND_HAS_NO_PARTNER:
        return (
          <HasPartner
            formData={{
              hasPartner: getHasPartner(capitalDeposit.partner),
            }}
            isSending={state.matches(State.SEND_HAS_NO_PARTNER)}
            onPrev={onPrev}
            onSubmit={(values) => send(model.events.SUBMIT_HAS_PARTNER(values))}
            progress={progress}
          />
        );

      case State.PARTNER_NAME:
      case State.SEND_PARTNER_NAME:
        return (
          <PartnerName
            formData={{
              partnerName: capitalDeposit.partner || undefined,
            }}
            isSending={state.matches(State.SEND_PARTNER_NAME)}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SUBMIT_PARTNER_NAME(values))
            }
            progress={progress}
          />
        );

      case `${State.ADDRESS}.${AddressState.SAME_ADDRESS}` as const:
      case `${State.ADDRESS}.${AddressState.SEND_SAME_ADDRESS}` as const:
        return (
          <SameAddress
            formData={{
              hasSameAddress,
            }}
            isSending={state.matches(
              `${State.ADDRESS}.${AddressState.SEND_SAME_ADDRESS}`,
            )}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SUBMIT_SAME_ADDRESS(values))
            }
            progress={progress}
            userProfile={userProfile}
          />
        );

      case `${State.ADDRESS}.${AddressState.COMPANY_ADDRESS}` as const:
      case `${State.ADDRESS}.${AddressState.SEND_COMPANY_ADDRESS}` as const:
        return (
          <CompanyAddress
            formData={
              hasSameAddress
                ? // If personal and company addresses are the same and user sees this page
                  // it means they made a mistake / wants change it,
                  // so show an empty form
                  { city: '', street: '', streetAddition: '', zip: '' }
                : {
                    city: companyProfile.city,
                    street: companyProfile.street,
                    streetAddition: companyProfile.streetAddition || undefined,
                    zip: companyProfile.zip,
                  }
            }
            isMandatee={isMandatee}
            isSending={state.matches(
              `${State.ADDRESS}.${AddressState.SEND_COMPANY_ADDRESS}`,
            )}
            onPrev={onPrev}
            onSubmit={(values) =>
              send(model.events.SUBMIT_COMPANY_ADDRESS(values))
            }
            progress={progress}
          />
        );

      case State.CERTIFICATE_WARNING:
      case State.SEND_CAPITAL_DEPOSIT_QUESTIONS:
        return (
          <CertificateWarning
            isMandatee={isMandatee}
            isSending={state.matches(State.SEND_CAPITAL_DEPOSIT_QUESTIONS)}
            onNext={() => send(model.events.SUBMIT_CAPITAL_DEPOSIT_QUESTIONS())}
            onPrev={onPrev}
            progress={progress}
          />
        );

      case State.DRAFT_STATUTES:
        return (
          <CapitalDepositDraftStatutes
            companyProfileId={moduleData.data.viewer.company.companyProfileId}
            isMandatee={isMandatee}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.DONE
                ? send(model.events.NEXT())
                : send(model.events.PREV())
            }
          />
        );

      case State.INIT:
      case State.DONE:
      case State.ABORT:
      case `${State.ADDRESS}.${AddressState.INIT}` as const:
      case `${State.ADDRESS}.${AddressState.DONE}` as const:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

interface CapitalDepositQuestionsProps {
  companyProfileId: string;
  isMandatee?: boolean; // If true, the locales will be adapted for a mandatee
}

const CapitalDepositQuestions = asBentoModule<CapitalDepositQuestionsProps>(
  ({ companyProfileId, isMandatee = false, onDone }) => {
    const query = useQuery(GetCapitalDepositQuestionsDocument, {
      variables: { companyProfileId },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(data) => {
          if (!isModuleDataValid(data)) {
            throw 'Capital deposit should not be null';
          }

          return (
            <CapitalDepositQuestionsComponent
              moduleData={{
                data,
                isMandatee,
              }}
              onDone={onDone}
            />
          );
        }}
      </SuspenseQuery>
    );
  },
);

export default CapitalDepositQuestions;
